import { promises as fs, Stats } from 'fs';
import { join, relative, extname } from 'path';
import {
  FileEntry,
  FolderEntry,
  WalkerEntry,
  WalkerOptions,
  FileHandler,
  FolderHandler,
  ErrorHandler,
  AsyncFilter,
  CollectorHandler,
} from './types';

export class FolderWalker<
  TFileResult = void,
  TFolderResult = void,
  TCollected = void
> {
  private options: WalkerOptions = {
    includeFiles: true,
    includeFolders: true,
    followSymlinks: false,
    includeHidden: false,
    readContents: false,
    customFilters: [],
  };

  private fileHandler?: FileHandler<TFileResult>;
  private folderHandler?: FolderHandler<TFolderResult>;
  private errorHandler?: ErrorHandler;
  private rootPath?: string;
  private collectingFiles = false;
  private collectingFolders = false;
  private fileCollector?: CollectorHandler<TFileResult, TCollected>;
  private folderCollector?: CollectorHandler<TFolderResult, TCollected>;

  withDepth(depth: number): this {
    this.options.maxDepth = depth;
    return this;
  }

  withFilesOnly(): this {
    this.options.includeFiles = true;
    this.options.includeFolders = false;
    return this;
  }

  withFoldersOnly(): this {
    this.options.includeFiles = false;
    this.options.includeFolders = true;
    return this;
  }

  withFilesAndFolders(): this {
    this.options.includeFiles = true;
    this.options.includeFolders = true;
    return this;
  }

  withFileContents(encoding: BufferEncoding = 'utf-8'): this {
    this.options.readContents = true;
    this.options.encoding = encoding;
    return this;
  }

  withExtensions(extensions: string[]): this {
    this.options.extensions = extensions.map(ext =>
      ext.startsWith('.') ? ext : `.${ext}`
    );
    return this;
  }

  withPattern(pattern: RegExp): this {
    this.options.pattern = pattern;
    return this;
  }

  exclude(patterns: string[]): this {
    this.options.exclude = patterns;
    return this;
  }

  include(patterns: string[]): this {
    this.options.include = patterns;
    return this;
  }

  withHidden(): this {
    this.options.includeHidden = true;
    return this;
  }

  withSymlinks(): this {
    this.options.followSymlinks = true;
    return this;
  }

  withMaxFileSize(bytes: number): this {
    this.options.maxFileSize = bytes;
    return this;
  }

  withFilter<T extends WalkerEntry>(filter: AsyncFilter<T>): this {
    if (!this.options.customFilters) {
      this.options.customFilters = [];
    }
    this.options.customFilters.push(filter as AsyncFilter<WalkerEntry>);
    return this;
  }

  onFile<T>(
    handler: FileHandler<T>
  ): FolderWalker<T, TFolderResult, TCollected> {
    const newWalker = this as any;
    newWalker.fileHandler = handler;
    return newWalker;
  }

  onFolder<T>(
    handler: FolderHandler<T>
  ): FolderWalker<TFileResult, T, TCollected> {
    const newWalker = this as any;
    newWalker.folderHandler = handler;
    return newWalker;
  }

  onError(handler: ErrorHandler): this {
    this.errorHandler = handler;
    return this;
  }

  withFileCollector(): FolderWalker<TFileResult, TFolderResult, TFileResult[]>;
  withFileCollector<R>(
    collector: CollectorHandler<TFileResult, R>
  ): FolderWalker<TFileResult, TFolderResult, R>;
  withFileCollector<R>(
    collector?: CollectorHandler<TFileResult, R>
  ): FolderWalker<TFileResult, TFolderResult, R | TFileResult[]> {
    const newWalker = this as any;
    newWalker.collectingFiles = true;
    newWalker.fileCollector = collector;
    return newWalker;
  }

  withFolderCollector(): FolderWalker<
    TFileResult,
    TFolderResult,
    TFolderResult[]
  >;
  withFolderCollector<R>(
    collector: CollectorHandler<TFolderResult, R>
  ): FolderWalker<TFileResult, TFolderResult, R>;
  withFolderCollector<R>(
    collector?: CollectorHandler<TFolderResult, R>
  ): FolderWalker<TFileResult, TFolderResult, R | TFolderResult[]> {
    const newWalker = this as any;
    newWalker.collectingFolders = true;
    newWalker.folderCollector = collector;
    return newWalker;
  }

  async execute(
    path: string
  ): Promise<TCollected extends void ? void : TCollected> {
    this.rootPath = path;

    const fileResults: TFileResult[] = [];
    const folderResults: TFolderResult[] = [];

    // Store original handlers
    const originalFileHandler = this.fileHandler;
    const originalFolderHandler = this.folderHandler;

    // Wrap handlers to collect results if needed
    if (this.collectingFiles && this.fileHandler) {
      this.fileHandler = async entry => {
        const result = await originalFileHandler!(entry);
        if (result !== undefined) {
          fileResults.push(result);
        }
        return result;
      };
    }

    if (this.collectingFolders && this.folderHandler) {
      this.folderHandler = async entry => {
        const result = await originalFolderHandler!(entry);
        if (result !== undefined) {
          folderResults.push(result);
        }
        return result;
      };
    }

    // Execute the walk
    await this.walk(path, 0);

    // Restore original handlers
    this.fileHandler = originalFileHandler;
    this.folderHandler = originalFolderHandler;

    // Return collected results
    if (this.collectingFiles) {
      if (this.fileCollector) {
        return (await this.fileCollector(fileResults)) as any;
      } else {
        return fileResults as any;
      }
    }

    if (this.collectingFolders) {
      if (this.folderCollector) {
        return (await this.folderCollector(folderResults)) as any;
      } else {
        return folderResults as any;
      }
    }

    return undefined as any;
  }

  files(path?: string): AsyncIterable<FileEntry> {
    if (path) this.rootPath = path;
    return this.createAsyncIterable<FileEntry>('file');
  }

  folders(path?: string): AsyncIterable<FolderEntry> {
    if (path) this.rootPath = path;
    return this.createAsyncIterable<FolderEntry>('folder');
  }

  entries(path?: string): AsyncIterable<WalkerEntry> {
    if (path) this.rootPath = path;
    return this.createAsyncIterable<WalkerEntry>('all');
  }

  private async *createAsyncIterable<T extends WalkerEntry>(
    type: 'file' | 'folder' | 'all'
  ): AsyncGenerator<T> {
    if (!this.rootPath) {
      throw new Error(
        'Root path not set. Call execute() or use iterator with path.'
      );
    }

    yield* this.walkIterator<T>(this.rootPath, 0, type);
  }

  private async *walkIterator<T extends WalkerEntry>(
    dirPath: string,
    depth: number,
    type: 'file' | 'folder' | 'all'
  ): AsyncGenerator<T> {
    if (this.options.maxDepth !== undefined && depth > this.options.maxDepth) {
      return;
    }

    try {
      const entries = await fs.readdir(dirPath);

      for (const entry of entries) {
        const fullPath = join(dirPath, entry);

        if (!this.shouldProcess(entry, fullPath)) {
          continue;
        }

        try {
          const stats = await fs.stat(fullPath);

          if (stats.isDirectory()) {
            if (
              (type === 'folder' || type === 'all') &&
              this.options.includeFolders
            ) {
              const folderEntry: FolderEntry = {
                path: fullPath,
                relativePath: relative(this.rootPath!, fullPath),
                name: entry,
                stats,
                depth,
                isFile: false,
                isDirectory: true,
              };

              // Apply custom filters
              if (await this.passesCustomFilters(folderEntry)) {
                if (this.folderHandler) {
                  await this.folderHandler(folderEntry);
                }
                yield folderEntry as T;
              }
            }

            yield* this.walkIterator<T>(fullPath, depth + 1, type);
          } else if (stats.isFile()) {
            if (
              (type === 'file' || type === 'all') &&
              this.options.includeFiles
            ) {
              const fileEntry = await this.createFileEntry(
                fullPath,
                entry,
                stats,
                depth
              );
              if (fileEntry && (await this.passesCustomFilters(fileEntry))) {
                if (this.fileHandler) {
                  await this.fileHandler(fileEntry);
                }
                yield fileEntry as T;
              }
            }
          }
        } catch (error) {
          this.handleError(error as Error, fullPath);
        }
      }
    } catch (error) {
      this.handleError(error as Error, dirPath);
    }
  }

  private async createFileEntry(
    fullPath: string,
    name: string,
    stats: Stats,
    depth: number
  ): Promise<FileEntry | null> {
    if (this.options.maxFileSize && stats.size > this.options.maxFileSize) {
      return null;
    }

    const ext = extname(name);
    if (this.options.extensions && !this.options.extensions.includes(ext)) {
      return null;
    }

    if (this.options.pattern && !this.options.pattern.test(name)) {
      return null;
    }

    const fileEntry: FileEntry = {
      path: fullPath,
      relativePath: relative(this.rootPath!, fullPath),
      name,
      ext,
      stats,
      depth,
      isFile: true,
      isDirectory: false,
    };

    if (this.options.readContents) {
      try {
        fileEntry.content = await fs.readFile(fullPath, this.options.encoding);
      } catch (error) {
        this.handleError(error as Error, fullPath);
      }
    }

    return fileEntry;
  }

  private async walk(dirPath: string, depth: number): Promise<void> {
    // Use the iterator for consistency
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for await (const _entry of this.walkIterator(dirPath, depth, 'all')) {
      // Entries are already processed by handlers in the iterator
    }
  }

  private shouldProcess(name: string, fullPath: string): boolean {
    if (!this.options.includeHidden && name.startsWith('.')) {
      return false;
    }

    if (this.options.exclude) {
      for (const pattern of this.options.exclude) {
        if (name === pattern || fullPath.includes(pattern)) {
          return false;
        }
      }
    }

    if (this.options.include) {
      let included = false;
      for (const pattern of this.options.include) {
        if (name === pattern || fullPath.includes(pattern)) {
          included = true;
          break;
        }
      }
      if (!included) {
        return false;
      }
    }

    return true;
  }

  private async passesCustomFilters(entry: WalkerEntry): Promise<boolean> {
    if (
      !this.options.customFilters ||
      this.options.customFilters.length === 0
    ) {
      return true;
    }

    for (const filter of this.options.customFilters) {
      try {
        const result = await filter(entry);
        if (!result) {
          return false;
        }
      } catch (error) {
        this.handleError(error as Error, entry.path);
        return false;
      }
    }

    return true;
  }

  private handleError(error: Error, path: string): void {
    if (this.errorHandler) {
      this.errorHandler(error, path);
    }
  }
}
